/**
 * AI Chat Mention System - Type Definitions
 *
 * Types and constants for @ (data) and # (page) mention references in AI chat.
 */

// ============================================================================
// Constants
// ============================================================================

export type MentionType = 'data' | 'page';
export type MentionTrigger = '@' | '#';

export const MAX_MENTION_SUGGESTIONS = 10;
export const MENTION_CACHE_TTL = 2 * 60 * 1000; // 2 minutes
export const MENTION_DEBOUNCE_MS = 200;
export const MAX_DRILL_DEPTH = 3; // group.schema.field

// Icon names for mention suggestions (Lucide icon names)
export const FIELD_TYPE_ICONS: Record<string, string> = {
  string: 'Type',
  number: 'Hash',
  boolean: 'ToggleLeft',
  date: 'Calendar',
  array: 'List',
  object: 'Braces',
};

export const DEFAULT_FIELD_ICON = 'CircleDot';
export const GROUP_ICON = 'FolderOpen';
export const SCHEMA_ICON = 'Database';
export const PAGE_ICON = 'FileText';
export const DEFAULT_PAGE_ICON = 'Home';

// ============================================================================
// Core Interfaces
// ============================================================================

/**
 * Autocomplete suggestion item for mention dropdown
 */
export interface MentionSegment {
  /** Display value (e.g., "Temperature Sensor", "Dashboard") */
  value: string;
  /** Reference path for insertion (e.g., "@greenhouse.temp", "#Dashboard") */
  reference: string;
  /** Unique identifier (schemaId, groupId, pageId, fieldName) */
  id: string;
  /** Description for display in dropdown */
  description?: string;
  /** Lucide icon name */
  icon?: string;
  /** Category for grouping (group, schema, field, page, Recent) */
  category: 'group' | 'schema' | 'field' | 'page' | 'Recent';
  /** Whether this item has children (can be drilled into) */
  hasChildren: boolean;
  /** Mention type for recent filtering */
  type?: MentionType;
  /** Additional metadata for resolution */
  metadata?: MentionMetadata;
}

/**
 * Detection result from analyzing cursor position
 */
export interface MentionContext {
  /** The trigger character (@ or #) */
  trigger: MentionTrigger;
  /** The mention type (data or page) */
  type: MentionType;
  /** Current search term after the last dot (for filtering suggestions) */
  searchTerm: string;
  /** Start position of the mention in the input string */
  startPosition: number;
  /** Current cursor position (end of mention) */
  endPosition: number;
  /** Path segments before the search term (e.g., ['greenhouse', 'temp'] for @greenhouse.temp.field) */
  path: string[];
  /** Raw text of the entire mention including trigger */
  rawText: string;
}

/**
 * Extensible metadata for mention resolution
 */
export interface MentionMetadata {
  /** For data mentions */
  schemaId?: string;
  groupId?: string;
  groupSlug?: string;
  fieldName?: string;
  fieldType?: string;

  /** For page mentions */
  pageId?: string;
  isDefaultPage?: boolean;

  /** Common */
  organizationId?: string;
}

/**
 * Parsed mention extracted from submitted message
 */
export interface ParsedMention {
  /** The mention type (data or page) */
  type: MentionType;
  /** Full reference path (e.g., "greenhouse.temp.reading", "Dashboard") */
  reference: string;
  /** Start position in the original message */
  startPosition: number;
  /** End position in the original message */
  endPosition: number;
  /** The raw text including trigger (e.g., "@greenhouse.temp.reading", "#Dashboard") */
  rawText: string;
}

/**
 * Fully resolved mention data for AI consumption
 */
export interface ResolvedMentionData {
  /** The mention type */
  type: MentionType;
  /** Original reference path */
  reference: string;

  /** For data mentions - resolved schema and field info */
  schema?: {
    id: string;
    name: string;
    displayName: string;
    description?: string;
    fields?: Array<{
      name: string;
      type: string;
      description?: string;
    }>;
  };
  group?: {
    id: string;
    name: string;
    slug: string;
    description?: string;
  };
  field?: {
    name: string;
    type: string;
    description?: string;
  };

  /** For page mentions - resolved page info */
  page?: {
    id: string;
    name: string;
    description?: string;
    isDefault: boolean;
  };
}

/**
 * Generic cache entry wrapper with timestamp
 */
export interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// ============================================================================
// Helper Types
// ============================================================================

/**
 * Mention service interface for consistent API across services
 */
export interface MentionService {
  getSuggestions(context: MentionContext): Promise<MentionSegment[]>;
  resolveReference(reference: string): Promise<ResolvedMentionData | null>;
  clearCache(): void;
}
